import { Script, Utils } from '@bsv/sdk';
import BitCom from '../bitcom/BitCom.js';
import B from '../bitcom/B.js';
import MAP from '../bitcom/MAP.js';
import AIP from '../bitcom/AIP.js';
// Constants for BSocial protocol
const BSOCIAL_APP_NAME = 'bsocial';
// Protocol addresses
const B_PROTOCOL_ADDRESS = '19HxigV4QyBv3tHpQVcUEQyq1pzZVdoAut';
const MAP_PROTOCOL_ADDRESS = '1PuQa7K62MiKCtssSLKy1kh56WWU7MtUR5';
const AIP_PROTOCOL_ADDRESS = '15PciHG22SNLQJXMoSUaWVi7WSqc7hCfva';
// Action types
export var BSocialActionType;
(function (BSocialActionType) {
    BSocialActionType["POST"] = "post";
    BSocialActionType["LIKE"] = "like";
    BSocialActionType["UNLIKE"] = "unlike";
    BSocialActionType["FOLLOW"] = "follow";
    BSocialActionType["UNFOLLOW"] = "unfollow";
    BSocialActionType["MESSAGE"] = "message";
})(BSocialActionType || (BSocialActionType = {}));
// Context types
export var BSocialContext;
(function (BSocialContext) {
    BSocialContext["TX"] = "tx";
    BSocialContext["CHANNEL"] = "channel";
    BSocialContext["BAP_ID"] = "bapID";
    BSocialContext["PROVIDER"] = "provider";
    BSocialContext["VIDEO_ID"] = "videoID";
    BSocialContext["GEOHASH"] = "geohash";
    BSocialContext["BTC_TX"] = "btcTx";
    BSocialContext["ETH_TX"] = "ethTx";
})(BSocialContext || (BSocialContext = {}));
/**
 * BSocial class implementing ScriptTemplate.
 *
 * This class provides methods to create BSocial protocol transactions for social media
 * actions like posts, likes, follows, etc. following BitcoinSchema.org standards.
 */
export default class BSocial {
    action;
    content;
    tags;
    identityKey;
    constructor(action, content, tags, identityKey) {
        this.action = action;
        this.content = content;
        this.tags = tags;
        this.identityKey = identityKey;
    }
    /**
     * Creates a BSocial locking script for various social actions
     *
     * @returns A locking script for the BSocial action
     */
    async lock() {
        const protocols = [];
        // Add B protocol for content (if present and not empty)
        if (this.content !== undefined && this.content !== null) {
            const bScript = B.text(this.content);
            // Extract only the protocol data (skip OP_RETURN and address)
            const bDataChunks = bScript.chunks.slice(2); // Skip OP_RETURN and address
            const bDataScript = new Script(bDataChunks);
            protocols.push({
                protocol: B_PROTOCOL_ADDRESS,
                script: bDataScript.toBinary(),
                pos: protocols.length
            });
        }
        // Add MAP protocol for action metadata
        const mapData = {
            app: BSOCIAL_APP_NAME,
            type: this.action.type
        };
        if (this.action.context) {
            mapData.context = this.action.context;
        }
        if (this.action.contextValue != null && this.action.contextValue !== '') {
            mapData.contextValue = this.action.contextValue;
        }
        if (this.action.subcontext) {
            mapData.subcontext = this.action.subcontext;
        }
        if (this.action.subcontextValue != null && this.action.subcontextValue !== '') {
            mapData.subcontextValue = this.action.subcontextValue;
        }
        // Add specific action data
        if (this.action.type === BSocialActionType.LIKE && 'txid' in this.action) {
            mapData.tx = this.action.txid;
        }
        if (this.action.type === BSocialActionType.FOLLOW && 'bapId' in this.action) {
            mapData.bapId = this.action.bapId;
        }
        // Add tags if present
        if ((this.tags != null) && this.tags.length > 0) {
            this.tags.forEach((tag, index) => {
                mapData[`tag${index}`] = tag;
            });
        }
        const mapScript = MAP.set(mapData);
        // Extract only the protocol data (skip OP_RETURN and address)
        const mapDataChunks = mapScript.chunks.slice(2); // Skip OP_RETURN and address
        const mapDataScript = new Script(mapDataChunks);
        protocols.push({
            protocol: MAP_PROTOCOL_ADDRESS,
            script: mapDataScript.toBinary(),
            pos: protocols.length
        });
        // Add AIP protocol for signature (if identity key provided)
        if (this.identityKey != null) {
            // Create data to sign (all protocol data)
            const signatureData = [];
            for (const proto of protocols) {
                signatureData.push(...Utils.toArray(proto.protocol, 'utf8'));
                signatureData.push(...proto.script);
                signatureData.push(0x7c); // '|' separator
            }
            const aipData = await AIP.sign(signatureData, this.identityKey);
            const aipScript = aipData.lock();
            // Extract only the AIP data from the script (skip OP_RETURN and protocol address)
            const aipChunks = aipScript.chunks.slice(2); // Skip OP_RETURN and AIP protocol address
            const aipDataScript = new Script(aipChunks);
            protocols.push({
                protocol: AIP_PROTOCOL_ADDRESS,
                script: aipDataScript.toBinary(),
                pos: protocols.length
            });
        }
        // Create BitCom transaction
        const bitcom = new BitCom(protocols);
        return bitcom.lock();
    }
    /**
     * Unlock method is not available for BSocial scripts, throws exception.
     */
    unlock() {
        throw new Error('Unlock is not supported for BSocial scripts');
    }
    /**
     * Creates a post transaction
     *
     * @param post The post data
     * @param tags Optional tags for the post
     * @param identityKey Optional private key for AIP signature
     * @returns A locking script for the post
     */
    static async createPost(post, tags, identityKey) {
        const bsocial = new BSocial(post, post.content, tags, identityKey);
        return await bsocial.lock();
    }
    /**
     * Creates a like transaction
     *
     * @param like The like data
     * @param identityKey Optional private key for AIP signature
     * @returns A locking script for the like
     */
    static async createLike(like, identityKey) {
        const bsocial = new BSocial(like, undefined, undefined, identityKey);
        return await bsocial.lock();
    }
    /**
     * Creates a follow transaction
     *
     * @param follow The follow data
     * @param identityKey Optional private key for AIP signature
     * @returns A locking script for the follow
     */
    static async createFollow(follow, identityKey) {
        const bsocial = new BSocial(follow, undefined, undefined, identityKey);
        return await bsocial.lock();
    }
    /**
     * Decodes a BSocial transaction script
     *
     * @param script The script to decode
     * @returns Decoded BSocial data
     */
    static decode(script) {
        try {
            // First decode the BitCom structure
            const bitcom = BitCom.decode(script);
            if (bitcom == null) {
                return null;
            }
            let content;
            let action;
            let tags = [];
            const attachments = [];
            let aip;
            // Process each protocol in the BitCom transaction
            for (const protocol of bitcom.protocols) {
                if (protocol.protocol === B_PROTOCOL_ADDRESS) {
                    // Parse B protocol data directly: DATA MEDIA_TYPE ENCODING [FILENAME]
                    const bScript = Script.fromBinary(protocol.script);
                    const chunks = bScript.chunks;
                    if (chunks.length >= 3) { // Need at least DATA, MEDIA_TYPE, ENCODING
                        const dataChunk = chunks[0];
                        const mediaTypeChunk = chunks[1];
                        const encodingChunk = chunks[2];
                        if ((dataChunk.data != null) && (mediaTypeChunk.data != null) && (encodingChunk.data != null)) {
                            const mediaType = Utils.toUTF8(mediaTypeChunk.data);
                            const encoding = Utils.toUTF8(encodingChunk.data);
                            if (mediaType === 'text/plain' && encoding === 'utf-8') {
                                content = Utils.toUTF8(dataChunk.data);
                            }
                            else {
                                attachments.push({
                                    type: mediaType,
                                    content: dataChunk.data,
                                    size: dataChunk.data.length,
                                    encoding
                                });
                            }
                        }
                    }
                }
                else if (protocol.protocol === MAP_PROTOCOL_ADDRESS) {
                    // Parse MAP protocol data directly: COMMAND key1 value1 key2 value2...
                    const mapScript = Script.fromBinary(protocol.script);
                    const chunks = mapScript.chunks;
                    if (chunks.length >= 1) {
                        // Read COMMAND (first chunk)
                        const cmdChunk = chunks[0];
                        if (cmdChunk.data != null) {
                            const cmd = Utils.toUTF8(cmdChunk.data);
                            // Handle SET command - read key-value pairs
                            if (cmd === 'SET') {
                                const mapData = {};
                                for (let i = 1; i < chunks.length; i += 2) {
                                    // Read key
                                    const keyChunk = chunks[i];
                                    if (keyChunk?.data == null)
                                        break;
                                    // Read value (next chunk)
                                    const valueChunk = chunks[i + 1];
                                    if (valueChunk?.data == null)
                                        break;
                                    const key = Utils.toUTF8(keyChunk.data);
                                    const value = Utils.toUTF8(valueChunk.data);
                                    mapData[key] = value;
                                }
                                const app = mapData.app;
                                if (app === BSOCIAL_APP_NAME) {
                                    const type = mapData.type;
                                    const context = mapData.context;
                                    const contextValue = mapData.contextValue;
                                    const subcontext = mapData.subcontext;
                                    const subcontextValue = mapData.subcontextValue;
                                    action = {
                                        app: BSOCIAL_APP_NAME,
                                        type,
                                        context,
                                        contextValue,
                                        subcontext,
                                        subcontextValue
                                    };
                                    // Extract specific action data
                                    if (type === BSocialActionType.LIKE) {
                                        const txid = mapData.tx;
                                        if (txid != null && txid !== '') {
                                            action.txid = txid;
                                        }
                                    }
                                    else if (type === BSocialActionType.FOLLOW) {
                                        const bapId = mapData.bapId;
                                        if (bapId != null && bapId !== '') {
                                            action.bapId = bapId;
                                        }
                                    }
                                    // Extract tags
                                    const tagEntries = Object.entries(mapData).filter(([key]) => key.startsWith('tag'));
                                    tags = tagEntries.map(([key, value]) => [key, value]);
                                }
                            }
                        }
                    }
                }
                else if (protocol.protocol === AIP_PROTOCOL_ADDRESS) {
                    // Extract AIP signature data - create BitCom structure for AIP.decode
                    const aipBitCom = {
                        protocols: [protocol],
                        scriptPrefix: []
                    };
                    const aipDecoded = AIP.decode(aipBitCom);
                    if (aipDecoded != null && aipDecoded.length > 0) {
                        aip = aipDecoded[0].data;
                    }
                }
            }
            // Must have action to be valid BSocial transaction
            if (action == null) {
                return null;
            }
            return {
                action,
                content,
                attachments,
                tags,
                aip
            };
        }
        catch (error) {
            return null;
        }
    }
    /**
     * Signs data with AIP protocol
     *
     * @param privateKey The private key to sign with
     * @param message The message to sign
     * @returns Base64 encoded signature
     */
    static async signAIP(privateKey, message) {
        const messageData = Utils.toArray(message, 'utf8');
        const aipData = await AIP.sign(messageData, privateKey);
        return Utils.toBase64(aipData.data.signature);
    }
    /**
     * Creates a reply transaction
     *
     * @param reply The reply data
     * @param replyTxId The transaction ID being replied to
     * @param tags Optional tags for the reply
     * @param identityKey Optional private key for AIP signature
     * @returns A locking script for the reply
     */
    static async createReply(reply, replyTxId, tags, identityKey) {
        const replyAction = {
            ...reply,
            context: BSocialContext.TX,
            contextValue: replyTxId
        };
        const bsocial = new BSocial(replyAction, reply.content, tags, identityKey);
        return await bsocial.lock();
    }
    /**
     * Creates a message transaction
     *
     * @param message The message data
     * @param identityKey Optional private key for AIP signature
     * @returns A locking script for the message
     */
    static async createMessage(message, identityKey) {
        const bsocial = new BSocial(message, message.content, undefined, identityKey);
        return await bsocial.lock();
    }
}
//# sourceMappingURL=BSocial.js.map